import { Fragment, useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  createColumnHelper,
  SortingState,
  RowSelectionState,
} from "@tanstack/react-table";
import TableCheckboxCol from "../../table/IndeterminateCheckbox";
import { UkvdVehicleRow } from "../../../data/useVehicleLookup";

const colHelper = createColumnHelper<UkvdVehicleRow>();
const cols = [
  TableCheckboxCol(false),
  colHelper.accessor("uvc", { header: "UVC" }),
  colHelper.accessor("make", { header: "Make" }),
  colHelper.accessor("range", { header: "Range" }),
  colHelper.accessor("fuelType", { header: "Fuel" }),
  colHelper.accessor("engineCapacityCC", { header: "CC" }),
  colHelper.accessor("kw", { header: "KW" }),
  colHelper.accessor("bhp", { header: "BHP" }),
  colHelper.accessor("startDate", { header: "Start" }),
  colHelper.accessor("endDate", { header: "End" }),
];

export default function UkvdResultsTable({
  rows,
  onSelectionChange,
  clearSelectionSignal,
}: {
  rows: UkvdVehicleRow[];
  onSelectionChange?: (rows: UkvdVehicleRow[]) => void;
  clearSelectionSignal?: number;
}) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  // Stable row ids: prefer UVC to keep selection consistent
  const getRowId = (row: UkvdVehicleRow, index: number) => row.uvc ?? String(index);

  const table = useReactTable({
    data: rows,
    columns: cols,
    getRowId,
    state: { sorting, rowSelection },
    enableRowSelection: true,
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  // bubble selection up to parent
  useEffect(() => {
    if (!onSelectionChange) return;
    const selected = table.getSelectedRowModel().flatRows.map((r) => r.original);
    onSelectionChange(selected);
  }, [rowSelection, table, onSelectionChange]);

  // external clear signal
  useEffect(() => {
    setRowSelection({});
  }, [clearSelectionSignal]);

  return (
    <div className="mt-4 bg-white rounded-md border border-gray-200">
      {/* Scroll container */}
      <div className="overflow-auto" style={{ maxHeight: "19rem" }}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="py-3.5 px-3 text-sm font-semibold text-left sticky top-0 bg-gray-50 z-10"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: " ▲",
                          desc: " ▼",
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className="divide-y divide-gray-100">
            {table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <tr className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-3 py-3 text-sm">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && <div className="p-6 text-sm text-gray-500 text-center">No results.</div>}
    </div>
  );
}
